import React, { useRef } from 'react';
import { Link } from 'react-router-dom';

function Support() {
  // Refs for scrolling to sections
  const accountRef = useRef(null);
  const billingRef = useRef(null);
  const plansRef = useRef(null);
  const scannerRef = useRef(null);
  const alertsRef = useRef(null);
  const technicalRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const supportCategories = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      ),
      title: 'Account Access',
      description: 'Help with login, password reset, and account settings.',
      topics: [
        'Forgot password or can\'t log in',
        'Update email or profile information',
        'Account security and two-factor authentication'
      ],
      ref: accountRef
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
          <line x1="1" y1="10" x2="23" y2="10"></line>
        </svg>
      ),
      title: 'Billing and Subscriptions',
      description: 'Questions about payments, invoices, and subscription management.',
      topics: [
        'Update payment method or billing information',
        'View invoices and payment history',
        'Cancel or change subscription plan'
      ],
      ref: billingRef
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
      ),
      title: 'Plans and Pricing',
      description: 'Learn about plan features, limits, and upgrade options.',
      topics: [
        'Compare Free, Pro, and Premium features',
        'Understand plan limits and usage',
        'Upgrade or downgrade your plan'
      ],
      ref: plansRef
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      ),
      title: 'Stock Scanner Reports',
      description: 'Help with running scans, interpreting results, and exporting reports.',
      topics: [
        'How to run and save stock scans',
        'Understanding Investment Score rankings',
        'Export PDF reports and analysis'
      ],
      ref: scannerRef
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
      ),
      title: 'Portfolio Alerts',
      description: 'Manage portfolio tracking, alerts, and notification preferences.',
      topics: [
        'Set up and customize portfolio alerts',
        'Manage alert thresholds and preferences',
        'Understand portfolio comparison features'
      ],
      ref: alertsRef
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
      ),
      title: 'Technical Issues',
      description: 'Report bugs, performance issues, or unexpected behavior.',
      topics: [
        'Page loading or display problems',
        'Data accuracy or sync issues',
        'Browser compatibility questions'
      ],
      ref: technicalRef
    }
  ];

  return (
    <div className="public-page">
      {/* Navigation - Premium Compact Header */}
      <nav className="public-nav">
        <div className="public-nav-container">
          <Link to="/" className="public-nav-brand">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="nav-brand-logo" 
            />
          </Link>
          <div className="public-nav-links">
            <Link to="/" className="public-nav-link">Home</Link>
            <Link to="/pricing" className="public-nav-link">Pricing</Link>
            <Link to="/login" className="public-nav-link">Log in</Link>
          </div>
        </div>
      </nav>

      {/* Support Page Content */}
      <div className="support-page-content">
        {/* Hero Section */}
        <div className="support-header">
          <div className="support-icon-wrapper">
            <svg className="support-hero-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <h1 className="support-title">How can we help?</h1>
          <p className="support-subtitle">
            Find answers to common questions or get in touch with our support team.
          </p>
        </div>

        {/* Support Categories Grid - Clickable Navigation */}
        <div className="support-categories">
          {supportCategories.map((category, index) => (
            <div 
              key={index} 
              className="support-category-card support-category-card-clickable"
              onClick={() => scrollToSection(category.ref)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  scrollToSection(category.ref);
                }
              }}
            >
              <div className="support-category-icon">
                {category.icon}
              </div>
              <h3 className="support-category-title">{category.title}</h3>
              <p className="support-category-description">{category.description}</p>
              <ul className="support-category-topics">
                {category.topics.map((topic, topicIndex) => (
                  <li key={topicIndex}>{topic}</li>
                ))}
              </ul>
              <div className="support-category-arrow">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Help Sections */}
        <div className="support-detailed-sections">
          {/* Account Access Section */}
          <div ref={accountRef} className="support-detail-section">
            <h2 className="support-detail-title">Account Access</h2>
            
            <div className="support-faq-item">
              <h3 className="support-faq-question">How do I reset my password?</h3>
              <p className="support-faq-answer">
                Currently, password reset functionality is being developed. If you've forgotten your password, 
                please contact support at <a href="mailto:support@blackpeakanalytics.com">support@blackpeakanalytics.com</a> with 
                your account email, and we'll help you regain access.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">Can I change my email address?</h3>
              <p className="support-faq-answer">
                Email address changes require manual verification for security. Please contact support at{' '}
                <a href="mailto:support@blackpeakanalytics.com">support@blackpeakanalytics.com</a> from both your 
                current and new email addresses to request a change.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">Why can't I log in?</h3>
              <p className="support-faq-answer">
                Common login issues include incorrect password, browser cookies disabled, or account not yet activated. 
                Ensure you're using the correct email and password. If you just signed up, check your email for a 
                confirmation link. Clear your browser cache and try again. If problems persist, contact support.
              </p>
            </div>
          </div>

          {/* Billing and Subscriptions Section */}
          <div ref={billingRef} className="support-detail-section">
            <h2 className="support-detail-title">Billing and Subscriptions</h2>
            
            <div className="support-faq-item">
              <h3 className="support-faq-question">How do I update my payment method?</h3>
              <p className="support-faq-answer">
                Go to your Billing page from the Dashboard menu. Click "Manage Subscription" to access the Stripe 
                Customer Portal where you can update your payment method, view invoices, and manage billing details 
                securely.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">Where can I find my invoices?</h3>
              <p className="support-faq-answer">
                All invoices are available through the Stripe Customer Portal. From your Billing page, click 
                "Manage Subscription" and navigate to the invoices section. You can download PDF copies of all 
                past invoices.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">How do I cancel my subscription?</h3>
              <p className="support-faq-answer">
                You can cancel anytime from your Billing page. Click "Cancel Subscription" to access the Stripe 
                Customer Portal, then select "Cancel subscription." Your access continues until the end of your 
                current billing period. You can reactivate before the period ends to avoid losing access.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">What happens when I cancel?</h3>
              <p className="support-faq-answer">
                When you cancel, you keep full access to your current paid plan until the end of your billing period. 
                After the paid period ends, your account is moved to the Free plan. Your saved scans, portfolios, 
                and holdings are preserved, but Free-plan limits will apply. For example, if you have more saved scans
                or portfolios than the Free plan allows, they are not deleted, but some actions such as saving new scans,
                creating new portfolios, or adding new holdings may be limited until you upgrade again.                                        
              </p>
            </div>
          </div>

          {/* Plans and Pricing Section */}
          <div ref={plansRef} className="support-detail-section">
            <h2 className="support-detail-title">Plans and Pricing</h2>
            
            <div className="support-faq-item">
              <h3 className="support-faq-question">What's included in the Free plan?</h3>
              <p className="support-faq-answer">
                The Free plan includes basic manual ticker scans for up to 10 specific tickers at a time, 
                1 portfolio, access to Investment Score rankings, and basic stock research pages.
                Full-market scans, where the platform scans the full S&P 500 universe and returns the top-ranked 
                opportunities, are available on Pro and Premium plans. The Free plan is designed for trying out 
                the platform and understanding the value-investing methodology before upgrading.                
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">What's the difference between Pro and Premium?</h3>
              <p className="support-faq-answer">
               Pro is for running full scans, tracking portfolios, using alerts, and managing your regular
               research workflow. Premium is for users who want deeper proof and analysis. It includes the 
               Strategy Lab, which shows how the strategy would have performed in past market
               periods and whether it beat a benchmark such as SPY. Premium also adds AI-powered interpretations 
               for fundamentals and backtesting, deeper explanations, advanced reports, 
               and one-click portfolio rebalancing tools.           
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">Can I upgrade or downgrade my plan?</h3>
              <p className="support-faq-answer">
                Yes, you can change plans anytime from your Billing page. Upgrades take effect immediately with 
                prorated billing. Downgrades take effect at the end of your current billing period. Use the 
                "Manage Subscription" button to access plan change options.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">Is there a discount for annual billing?</h3>
              <p className="support-faq-answer">
               Yes. Choose annual billing and you effectively get about one month free compared with paying month
               to month. Pro is $209/year instead of $228 paid monthly, and Premium is $439.89/year instead of $479.88 paid monthly. You can change your billing cycle anytime from your Billing page.
              </p>
            </div>
          </div>

          {/* Stock Scanner Reports Section */}
          <div ref={scannerRef} className="support-detail-section">
            <h2 className="support-detail-title">Stock Scanner Reports</h2>
            
            <div className="support-faq-item">
              <h3 className="support-faq-question">How do I run a stock scan?</h3>
              <p className="support-faq-answer">
               Free users can run manual scans by entering up to 10 specific tickers, separated by commas. The scanner ranks only the tickers entered. Pro and Premium users can leave the ticker field blank to scan the full S&P 500 universe and return the top-ranked stocks. Optional filters can narrow the scan universe before the Investment Score ranks the results. 
                              
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">What is the Investment Score?</h3>
              <p className="support-faq-answer">
                The Investment Score is our proprietary ranking metric combining six fundamental factors: valuation, 
                profitability, balance sheet strength, growth/cash flow, risk/stability, and return consistency. 
                Higher scores indicate stocks that better align with value-investing principles. See our Methodology 
                page for detailed scoring criteria.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">How do I save and compare scans?</h3>
              <p className="support-faq-answer">
               After running a scan, click "Save Scan" to store the results. Saved scans appear on your Saved Scans page with timestamps. You can compare scans to see rank changes and Investment Score movement over time. Use "Previous scan" to compare your latest saved scan with the scan immediately before it. Use "7 days ago" or "30 days ago" to compare against an older scan from that time period when available. Use "Manual" when you want to select two specific saved scans yourself, then click "Compare Selected Scans." Comparisons are most useful when scans are from different dates; scans saved close together may show little or no change.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">Can I export scan results to PDF?</h3>
              <p className="support-faq-answer">
                Yes, all users can export PDF reports. From your Dashboard, click on a saved scan to view details, 
                then click "Export PDF Report." The PDF includes ranked stocks, Investment Scores, and key metrics. 
                You can also export portfolio comparison reports if you've compared a scan with a portfolio.
              </p>
            </div>
          </div>

          {/* Portfolio Alerts Section */}
          <div ref={alertsRef} className="support-detail-section">
            <h2 className="support-detail-title">Portfolio Alerts</h2>
            
            <div className="support-faq-item">
              <h3 className="support-faq-question">How do portfolio alerts work? (Pro/Premium)</h3>
              <p className="support-faq-answer">
                Portfolio alerts automatically monitor your holdings against the latest scan results. You'll receive 
                notifications when holdings fall below your Investment Score threshold, drop outside your preferred 
                rank range, or when new high-ranking opportunities appear. Alerts appear in the Notification Center 
                on your Dashboard.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">How do I customize alert thresholds? (Pro/Premium)</h3>
              <p className="support-faq-answer">
                On your Dashboard, expand the "Portfolio Alert Preferences" section. You can set: minimum Investment 
                Score threshold (default 50), preferred rank range (default Top 10), minimum score for new opportunities, 
                and whether to include overbought stocks. Click "Save Preferences" to apply changes.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">What does "Acknowledged" mean for alerts?</h3>
              <p className="support-faq-answer">
                When you acknowledge an alert, you're indicating you've reviewed it and made a decision. Acknowledged 
                alerts move to a separate section in the Notification Center but remain visible for your records. 
                You can restore acknowledged alerts if you want them to appear as active again.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">How do I add stocks to my portfolio?</h3>
              <p className="support-faq-answer">
                You can add stocks from scan results, the Stock Research page, or directly from portfolio alerts. 
                Click "Add to Portfolio," select your portfolio, enter quantity and average buy price, then confirm. 
                The stock will appear in your portfolio with calculated gain/loss based on current prices.
              </p>
            </div>
          </div>

          {/* Technical Issues Section */}
          <div ref={technicalRef} className="support-detail-section">
            <h2 className="support-detail-title">Technical Issues</h2>
            
            <div className="support-faq-item">
              <h3 className="support-faq-question">The page won't load or is very slow</h3>
              <p className="support-faq-answer">
                First, try refreshing the page (Ctrl+R or Cmd+R). If that doesn't work, clear your browser cache 
                and cookies, then try again. Ensure you're using a modern browser (Chrome, Firefox, Safari, or Edge). 
                If problems persist, try a different browser or device to isolate the issue.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">Stock data seems incorrect or outdated</h3>
              <p className="support-faq-answer">
                Our fundamentals data updates periodically from reliable sources. If you notice a specific data 
                discrepancy, please email <a href="mailto:support@blackpeakanalytics.com">support@blackpeakanalytics.com</a> with 
                the ticker symbol, the field that appears incorrect, and what you expected to see. We'll investigate 
                and update our data sources if needed.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">Which browsers are supported?</h3>
              <p className="support-faq-answer">
                Black Peak Analytics works best on the latest versions of Chrome, Firefox, Safari, and Edge. We 
                recommend keeping your browser updated for the best experience. Internet Explorer is not supported. 
                Mobile browsers are supported but the desktop experience is optimized for research workflows.
              </p>
            </div>

            <div className="support-faq-item">
              <h3 className="support-faq-question">I found a bug - how do I report it?</h3>
              <p className="support-faq-answer">
                We appreciate bug reports! Email <a href="mailto:support@blackpeakanalytics.com">support@blackpeakanalytics.com</a> with: 
                (1) what you were trying to do, (2) what happened instead, (3) your browser and operating system, 
                and (4) any error messages you saw. Screenshots are very helpful. We'll investigate and follow up.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="support-contact-section">
          <div className="support-contact-card">
            <div className="support-contact-header">
              <div className="support-contact-icon-wrapper">
                <svg className="support-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <h2 className="support-contact-title">Still need help?</h2>
            </div>
            <p className="support-contact-description">
              Can't find what you're looking for? Our support team is here to help. 
              Send us an email and we'll get back to you as soon as possible.
            </p>
            <div className="support-contact-info">
              <div className="support-contact-method">
                <span className="support-contact-label">Email:</span>
                <a href="mailto:support@blackpeakanalytics.com" className="support-contact-link">
                  support@blackpeakanalytics.com
                </a>
              </div>
              <div className="support-contact-method">
                <span className="support-contact-label">Response Time:</span>
                <span className="support-contact-value">Within 24-48 hours</span>
              </div>
            </div>
            <p className="support-contact-note">
              Please include your account email and a detailed description of your issue 
              to help us assist you more quickly.
            </p>
          </div>
        </div>

      </div>

      {/* Footer */}
      <footer className="public-footer public-footer-compact">
        <div className="public-footer-container public-footer-container-compact">
          <Link to="/" className="footer-brand-compact">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="footer-brand-logo" 
            />
          </Link>
          <nav className="footer-nav-compact" aria-label="Footer navigation">
            <Link to="/" className="public-footer-link">Home</Link>
            <Link to="/pricing" className="public-footer-link">Pricing</Link>
            <Link to="/support" className="public-footer-link">Support</Link>
            <Link to="/privacy" className="public-footer-link">Privacy</Link>
            <Link to="/terms" className="public-footer-link">Terms</Link>
            <Link to="/investment-disclaimer" className="public-footer-link">Disclaimer</Link>
          </nav>
          <p className="footer-disclaimer footer-disclaimer-compact">Independent research tool. Not financial advice.</p>
        </div>
      </footer>
    </div>
  );
}

export default Support;
